import Phaser from 'phaser'
import SettingsMenu from '../classes/SettingsMenu'

export default class StartScreen extends Phaser.Scene {
  private music!: Phaser.Sound.BaseSound
  private audioPrompt?: Phaser.GameObjects.Text
  private settingsMenu!: SettingsMenu

  constructor() {
    super('startScreen')
  }

  private startMusic(): void {
    this.music = this.sound.get('track1') ?? this.sound.add('track1')
    if (this.music.isPlaying) {
      return
    }

    const musicConfig = {
      mute: false,
      volume: 0.4,
      seek: 0,
      loop: true,
      delay: 0,
    }

    const playMusic = () => {
      if (this.music.isPlaying) {
        return
      }

      this.audioPrompt?.destroy()
      this.audioPrompt = undefined
      this.music.play(musicConfig)
    }

    if (this.sound.locked) {
      this.audioPrompt = this.add
        .text(16, 48, 'Click or press any key to enable audio', {
          font: '16px monospace',
          color: '#ffffff',
          backgroundColor: '#000000',
          padding: { x: 8, y: 4 },
        })
        .setScrollFactor(0)

      const unlockAndPlay = () => {
        const manager = this.sound as
          | Phaser.Sound.NoAudioSoundManager
          | Phaser.Sound.HTML5AudioSoundManager
          | Phaser.Sound.WebAudioSoundManager
        if ('context' in manager) {
          void manager.context.resume()
        }
        playMusic()
      }

      this.input.once('pointerdown', unlockAndPlay)
      this.input.keyboard?.once('keydown', unlockAndPlay)
      return
    }

    playMusic()
  }

  create() {
    this.startMusic()
    this.settingsMenu = new SettingsMenu(this, { showOpenButton: false })

    const centerX = this.cameras.main.centerX
    const centerY = this.cameras.main.centerY

    // Title
    this.add
      .text(centerX, centerY - 120, 'INDUSTRIAL BOY', {
        fontSize: '72px',
        color: '#ff6b6b',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)

    // Start button
    const buttonWidth = 200
    const buttonHeight = 60
    const buttonX = centerX
    const buttonY = centerY + 100

    const buttonBg = this.add
      .rectangle(buttonX, buttonY, buttonWidth, buttonHeight, 0x351f1b)
      .setStrokeStyle(3, 0xff6b6b)
      .setInteractive({ useHandCursor: true })

    const buttonText = this.add
      .text(buttonX, buttonY, 'START GAME', {
        fontSize: '28px',
        color: '#ff6b6b',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)

    const settingsButtonY = buttonY + 80

    const settingsButtonBg = this.add
      .rectangle(buttonX, settingsButtonY, buttonWidth, buttonHeight, 0x351f1b)
      .setStrokeStyle(3, 0xff6b6b)
      .setInteractive({ useHandCursor: true })

    const settingsButtonText = this.add
      .text(buttonX, settingsButtonY, 'SETTINGS', {
        fontSize: '28px',
        color: '#ff6b6b',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)

    // Button hover effect
    buttonBg.on('pointerover', () => {
      buttonBg.setFillStyle(0x553535)
      buttonText.setFill('#ffff00')
    })

    buttonBg.on('pointerout', () => {
      buttonBg.setFillStyle(0x351f1b)
      buttonText.setFill('#ff6b6b')
    })

    buttonBg.on('pointerdown', () => {
      this.startGame()
    })

    settingsButtonBg.on('pointerover', () => {
      settingsButtonBg.setFillStyle(0x553535)
      settingsButtonText.setFill('#ffff00')
    })

    settingsButtonBg.on('pointerout', () => {
      settingsButtonBg.setFillStyle(0x351f1b)
      settingsButtonText.setFill('#ff6b6b')
    })

    settingsButtonBg.on('pointerdown', () => {
      this.settingsMenu.toggleMenu()
    })

    // Also allow pressing Space or Enter to start
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.settingsMenu.isOpen()) {
        return
      }
      if (event.code === 'Space' || event.code === 'Enter') {
        this.startGame()
      }
    })
  }

  private startGame() {
    this.scene.start('tutorial_1')
  }
}
